import React__default from 'react';
import { useFormContext, useController, useWatch } from 'react-hook-form';
import { Switch } from '../../atoms/Switch.js';
import { TitleGroup } from '../TitleGroup.js';
import { InfoIconWithTooltip } from '../Tooltip/ContentWithTooltip.js';
import { getFieldAdapter } from './fieldAdapters.js';
import './FormItem.scss';

/**
 * FormItem component for React Hook Form-based forms.
 *
 * This component automatically connects to the form state when used inside a Form.
 * It uses useController to manage field state and automatically injects the correct props
 * into child components based on their type.
 *
 * @example
 * ```tsx
 * const schema = z.object({ email: z.string().email() });
 *
 * <Form schema={schema} onSubmit={handleSubmit}>
 *   <FormItem schemaField="email" labelText="Email Address" required>
 *     <TextInput placeholder="Enter your email" />
 *   </FormItem>
 *   <Button type="submit">Submit</Button>
 * </Form>
 * ```
 */
function FormItem({ schemaField, labelText, descriptionText, children, id, className, required, orientation: passedInOrientation, useTooltipForDescription = true, hideLabel = false, isDisabled, linkedField, showFieldToggle, skipAdapterProps, }) {
    const { setValue } = useFormContext();
    const { field, fieldState } = useController({
        name: schemaField,
        shouldUnregister: false,
    });
    // Toggle guard state management - initialize based on field value if showFieldToggle is enabled
    const [isToggleEnabled, setIsToggleEnabled] = React__default.useState(() => {
        if (!showFieldToggle)
            return false;
        // If field has a truthy value, toggle should be on; otherwise off
        return !!field.value;
    });
    // Sync toggle state when field value changes externally (e.g., form reset, programmatic updates)
    React__default.useEffect(() => {
        if (showFieldToggle) {
            setIsToggleEnabled(!!field.value);
        }
    }, [showFieldToggle, field.value]);
    // Clear field value when user turns toggle off
    const handleToggleChange = React__default.useCallback((value) => {
        // Don't allow toggle change if field is disabled
        if (isDisabled) {
            return;
        }
        setIsToggleEnabled(value);
        if (!value && field.value) {
            // User turned toggle off - clear the field
            setValue(schemaField, undefined, {
                shouldValidate: false,
                shouldDirty: true,
            });
        }
    }, [field.value, schemaField, setValue, isDisabled]);
    // Watch the linked field if specified
    const linkedValue = useWatch({
        name: linkedField,
        disabled: !linkedField,
    });
    // Sync when linked field changes
    React__default.useEffect(() => {
        if (linkedField && linkedValue !== undefined) {
            setValue(schemaField, linkedValue, {
                shouldValidate: false,
                shouldDirty: true,
            });
        }
    }, [linkedField, linkedValue, schemaField, setValue]);
    const errorMessage = fieldState.error?.message;
    const showInvalidFeedback = !!errorMessage;
    // Check if the required prop was passed or fallback to child's required
    const isRequired = required !== undefined ? !!required : !!children.props.required;
    const generatedId = React__default.useId();
    const fieldId = id || generatedId;
    const descriptionId = descriptionText ? `${fieldId}-description` : undefined;
    // Detect if the children is a checkbox to determine default orientation
    const isCheckbox = React__default.useMemo(() => {
        if (!children)
            return false;
        // Check if it's a native checkbox input
        if (children.type === 'input' && children.props.type === 'checkbox') {
            return true;
        }
        // Check if it's a Checkbox component
        if (children.type && typeof children.type === 'function') {
            const componentName = children.type.displayName || children.type.name;
            if (componentName === 'Checkbox' || componentName === 'Checks') {
                return true;
            }
        }
        return false;
    }, [children]);
    // Set default orientation based on input type
    const defaultOrientation = isCheckbox ? 'horizontal' : 'vertical';
    const orientation = passedInOrientation ?? defaultOrientation;
    // Get adapted props for the child component
    const adaptedProps = getFieldAdapter(children, field, fieldState);
    // Check which props are already set on the child
    // If they're already set, don't override them (allows custom handling like SwitchFormField's objectValue)
    const existingProps = children.props && typeof children.props === 'object' ? children.props : {};
    const propsToInject = {
        id: fieldId,
        ...(descriptionId && { 'aria-describedby': descriptionId }),
        ...(required !== undefined ? { required } : {}),
        ...(isDisabled !== undefined ? { isDisabled } : {}),
    };
    // Only inject adapted props that:
    // 1. Are not explicitly skipped via skipAdapterProps
    // 2. Are not already set on the child (preserves explicit props)
    const skipSet = new Set(skipAdapterProps || []);
    Object.keys(adaptedProps).forEach((key) => {
        const propKey = key;
        if (!skipSet.has(propKey) && !(key in existingProps)) {
            propsToInject[key] = adaptedProps[propKey];
        }
    });
    // Clone child and inject form props
    const childWithProps = React__default.cloneElement(children, propsToInject);
    // We use hidden layout placeholder text to ensure that if/when an error
    // is shown, the page doesn't change layout to accommodate the error.
    const layoutPlaceholderText = 'Layout placeholder';
    const showDescriptionAsTooltip = useTooltipForDescription && descriptionText;
    const showDescriptionBelow = !useTooltipForDescription && descriptionText;
    const showingLayoutPlaceholder = !showInvalidFeedback && !showDescriptionBelow;
    // Render description content (below field, not as tooltip)
    const description = (React__default.createElement(React__default.Fragment, null,
        !showInvalidFeedback && showDescriptionBelow && (React__default.createElement("p", { id: descriptionId, className: 'form-field-description--rev' }, descriptionText)),
        showingLayoutPlaceholder && (React__default.createElement("p", { className: 'form-field-description--rev form-field-placeholder-hidden--rev', "aria-hidden": 'true' }, layoutPlaceholderText))));
    // Render tooltip icon if description should be shown as tooltip
    const tooltipIcon = showDescriptionAsTooltip ? (React__default.createElement(InfoIconWithTooltip, { tooltipContent: descriptionText, tooltipProps: {
            placement: 'top',
        } })) : null;
    const horizontalLayout = (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("div", { className: 'form-field-control--rev' }, childWithProps),
        labelText && !hideLabel && (React__default.createElement("label", { className: 'form-field-label--rev', htmlFor: fieldId },
            React__default.createElement("span", { className: 'form-field-label-text--rev' },
                labelText,
                tooltipIcon && (React__default.createElement("span", { className: 'form-field-tooltip-icon--rev' }, tooltipIcon))),
            description))));
    const verticalLayout = (React__default.createElement(React__default.Fragment, null,
        labelText && !hideLabel && (React__default.createElement("label", { className: 'form-field-label--rev', htmlFor: fieldId },
            React__default.createElement("span", { className: 'form-field-label-text--rev' },
                labelText,
                tooltipIcon && (React__default.createElement("span", { className: 'form-field-tooltip-icon--rev' }, tooltipIcon))))),
        React__default.createElement("div", { className: 'form-field-control--rev' }, childWithProps),
        description));
    // If toggleGuard is provided, render with TitleGroup
    if (showFieldToggle) {
        return (React__default.createElement("div", { "data-rev-component": 'form-field', "data-rev-invalid": !!showInvalidFeedback, "data-rev-required-field": isRequired, className: className },
            React__default.createElement(TitleGroup, { title: labelText, description: descriptionText, level: 'minor', titleWeight: 'regular', leftComponent: React__default.createElement(Switch, { isSelected: isToggleEnabled, onChange: handleToggleChange, isDisabled: isDisabled, "aria-label": labelText ? `Toggle ${labelText}` : 'Toggle field' }) }),
            isToggleEnabled && (React__default.createElement("div", { className: 'form-field-content--rev form-field-toggle-content--rev' },
                React__default.createElement("div", { className: 'form-field-control--rev' }, childWithProps)))));
    }
    return (React__default.createElement("div", { "data-rev-component": 'form-field', "data-rev-invalid": !!showInvalidFeedback, "data-rev-required-field": isRequired, "data-rev-orientation": orientation, className: className }, orientation === 'horizontal' ? horizontalLayout : verticalLayout));
}
FormItem.displayName = 'FormItem';

export { FormItem };
//# sourceMappingURL=FormItem.js.map
